import { Box, Button, InputBase, Typography} from "@mui/material";
import {  useState, useEffect } from "react";
import { Toast } from "primereact/toast";
import { useRef } from "react";
import { InputTextarea } from "primereact/inputtextarea";
import CustomAxios from "../../utils/CustomAxios";
import * as XLSX from "xlsx";
import "../../App.css";
import { Dropdown } from "primereact/dropdown";
import { useSelector } from "react-redux";
import { MultiSelect } from "primereact/multiselect";

export function Campaign_Push_notification() {
    const [title, setTitle] = useState()
    const [message, setMessage] = useState()
    const [product_id, setProduct_id] = useState({})
    const [errors, setErrors] = useState({})
    const products = useSelector(state => state.products.products);
    const [loading, setLoading] = useState(false)
    const toast = useRef(null);
    const [inventory_id, setInventory_id] = useState(null)
    const [stall_id, setStall_id] = useState(null) 
    const [stalls, setStalls] = useState([])  
    const [selectedFranchise, setSelectedFranchise] = useState(null) 
    const inventory = useSelector(state => state.inventory.inventory);
    const selectedHub = useSelector(state => state.user.selected_hub); // gets ID
    const user_role = localStorage.getItem('role');
    const [subscriptionPlans, setSubscriptionPlans] = useState([]);
    const [selectedPlanIds, setSelectedPlanIds] = useState([]);
    const [audience, setAudience] = useState('default');
    const [preview, setPreview] = useState(null);
    const [previewLoading, setPreviewLoading] = useState(false);
    const [exporting, setExporting] = useState(false);

    const audienceOptions = [
        { label: 'Default (hub/stall users — existing behavior)', value: 'default' },
        { label: 'Active plan subscribers', value: 'subscribers' },
        { label: 'Expired subscribers (nudge to rebuy)', value: 'expired_subscribers' },
        { label: 'Non-subscribers (promote plan purchase)', value: 'non_subscribers' },
    ];

    useEffect(() => {
        const loadPlans = async () => {
            try {
                const res = await CustomAxios.get('subscriptions/plans/', {
                    params: { is_active: true },
                });
                const plans = Array.isArray(res.data) ? res.data : (res.data?.results || []);
                setSubscriptionPlans(plans.map((p) => ({
                    id: p.id,
                    name: `${p.plan_name}${p.base_price != null ? ` (₹${p.base_price})` : ''}`,
                })));
            } catch (e) {
                console.error('Failed to load subscription plans for campaign', e);
            }
        };
        loadPlans();
    }, []);

    // Any change to the targeting invalidates the counted preview, so the
    // export cannot be taken from a stale audience.
    useEffect(() => { setPreview(null); }, [audience, selectedPlanIds, inventory_id, stall_id]);

    const buildTargetPayload = (inventoryIds) => {
        const payload = {
            inventory_ids: inventoryIds,
            audience: audience || 'default',
        };
        if (selectedFranchise === 2 && stall_id && stall_id.length > 0) {
            payload.stall_ids = stall_id;
        }
        if (selectedPlanIds && selectedPlanIds.length > 0) {
            payload.subscription_plan_ids = selectedPlanIds;
        }
        if (audience === 'non_subscribers') {
            payload.target_non_subscribers = true;
        }
        return payload;
    };

    const resolveInventoryIds = () => {
        let inventoryIds = inventory_id;
        if (user_role === 'supervisor' || user_role === 'admin') {
            if (Array.isArray(inventory_id) && inventory_id.length > 0) {
                inventoryIds = inventory_id;
            } else if (hasSingleAssignedHub) {
                inventoryIds = [assignedHubs[0]];
            } else {
                const selectedHubRaw = localStorage.getItem('selected_hub');
                try {
                    const parsedHub = JSON.parse(selectedHubRaw || '[]');
                    if (Array.isArray(parsedHub)) {
                        inventoryIds = parsedHub;
                    } else {
                        inventoryIds = [parsedHub];
                    }
                } catch (error) {
                    inventoryIds = [selectedHubRaw];
                }
            }
        } else if (Array.isArray(inventory_id)) {
            inventoryIds = inventory_id;
        } else {
            inventoryIds = [inventory_id];
        }
        return inventoryIds;
    };

    /**
     * Download the exact recipient list as .xlsx.
     *
     * Fetches the audience with no limit so the sheet is the full list, not the
     * capped preview, and asks the same endpoint the count came from — so the
     * file always matches the number shown on screen.
     */
    const handleDownloadRecipients = async () => {
        setExporting(true);
        try {
            const inventoryIds = resolveInventoryIds();
            const payload = { ...buildTargetPayload(inventoryIds), limit: 0 };
            const res = await CustomAxios.post('campaign_push_preview/', payload);
            const rows = res.data?.recipients || [];

            if (!rows.length) {
                toast.current.show({
                    severity: 'warn',
                    summary: 'Nothing to export',
                    detail: 'No users match the selected audience',
                    life: 3000,
                });
                return;
            }

            const sheetRows = rows.map((r) => ({
                'User ID': r.user_id,
                Name: r.name,
                Email: r.email,
                Mobile: r.mobile,
                Inventory: r.inventory,
                'Inventory ID': r.inventory_id ?? '',
                'To Send': r.to_send,
            }));

            const sheet = XLSX.utils.json_to_sheet(sheetRows);
            sheet['!cols'] = [
                { wch: 10 }, { wch: 26 }, { wch: 30 },
                { wch: 16 }, { wch: 24 }, { wch: 14 }, { wch: 10 },
            ];
            const book = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(book, sheet, 'Recipients');

            const stamp = new Date().toISOString().slice(0, 16).replace(/[:T]/g, '-');
            XLSX.writeFile(book, `campaign-recipients-${stamp}.xlsx`);

            toast.current.show({
                severity: 'success',
                summary: 'Downloaded',
                detail: `${rows.length} recipient(s) exported`,
                life: 3000,
            });
        } catch (e) {
            console.error(e);
            toast.current.show({
                severity: 'error',
                summary: 'Export failed',
                detail: 'Could not download the recipient list',
                life: 3000,
            });
        }
        setExporting(false);
    };

    const handlePreviewCount = async () => {
        setPreviewLoading(true);
        try {
            const inventoryIds = resolveInventoryIds();
            const payload = buildTargetPayload(inventoryIds);
            const res = await CustomAxios.post('campaign_push_preview/', payload);
            setPreview(res.data || null);
            toast.current.show({
                severity: 'info',
                summary: 'Audience preview',
                detail: `${res.data?.eligible_users ?? 0} users (${res.data?.with_fcm_token ?? 0} with push token)`,
                life: 3500,
            });
        } catch (e) {
            console.error(e);
            toast.current.show({
                severity: 'error',
                summary: 'Preview failed',
                detail: 'Could not load audience count',
                life: 3000,
            });
        }
        setPreviewLoading(false);
    };
    
    // Get assigned hubs for role-based filtering
    const assignedHubs = JSON.parse(localStorage.getItem('hub') || '[]');
    const hasSingleAssignedHub = Array.isArray(assignedHubs) && assignedHubs.length === 1;
    const hasMultipleAssignedHubs = Array.isArray(assignedHubs) && assignedHubs.length > 1;
    
    if (['customer_engagement_admin', 'supervisor'].includes(user_role)) {
        console.log('Campaign Push - Debug for:', user_role);
        console.log('Role:', user_role);
        console.log('assignedHubs from localStorage:', assignedHubs);
        console.log('Total products:', products.length);
        
        if (user_role === 'supervisor') {
            const selectedFranchiseId = localStorage.getItem('selected_franchise_id');
            const isFoodFixSupervisor = selectedFranchiseId && parseInt(selectedFranchiseId) === 2;
            console.log('Campaign Push - Supervisor FoodFix Check:', {
                selectedFranchiseId,
                isFoodFixSupervisor,
                inventory: inventory?.map(hub => ({ id: hub.value, name: hub.name, franchise_id: hub.franchise_id }))
            });
        }
    }
    
    // Debug logging for stall functionality
    // console.log('Campaign Push Debug:');
    // console.log('inventory:', inventory);
    // console.log('inventory_id:', inventory_id);
    // console.log('selectedFranchise:', selectedFranchise);
    // console.log('stalls:', stalls);
    
    // Filter inventory based on user role and assigned hubs
    const filteredInventory = (inventory || []).map((inv) => {
        const isAssigned = assignedHubs.includes(inv.value);
        return {
            ...inv,
            name: inv.name ?? 'Not assigned hub',
            disabled: ['admin', 'supervisor', 'customer_engagement_admin'].includes(user_role) ? !isAssigned : false
        };
    }).filter((inv) => {
        // For admin, supervisor, and customer engagement admin, only show assigned hubs
        if (['admin', 'supervisor', 'customer_engagement_admin'].includes(user_role)) {
            return assignedHubs.includes(inv.value);
        }
        return true; 
    });
    
    const filteredProducts = (products || []).filter((product) => {
        if (user_role === 'supervisor') {
            const selectedHubs = inventory_id || [];
            if (Array.isArray(selectedHubs) && selectedHubs.length > 0) {
                // Check multiple ways products can be associated with hubs
                const hasInventoryIdMatch = selectedHubs.includes(product.inventory_id);
                const hasInventoryArrayMatch = product.inventory && product.inventory.some(inv => selectedHubs.includes(inv.id));
                const hasCapacityDetailsMatch = product.capacitydetails && Object.keys(product.capacitydetails).some(hubId => selectedHubs.includes(parseInt(hubId)));
                
                console.log('Supervisor Product Filter Debug:', {
                    productName: product.name || product.item_name,
                    productId: product.id,
                    selectedHubs,
                    productInventoryId: product.inventory_id,
                    productInventory: product.inventory,
                    productCapacityDetails: product.capacitydetails,
                    hasInventoryIdMatch,
                    hasInventoryArrayMatch,
                    hasCapacityDetailsMatch,
                    finalResult: hasInventoryIdMatch || hasInventoryArrayMatch || hasCapacityDetailsMatch
                });
                
                return hasInventoryIdMatch || hasInventoryArrayMatch || hasCapacityDetailsMatch;
            }
            return false; 
        } else if (user_role === 'super_admin') {
            return true;
        } else {
            const selectedHubs = inventory_id || [];
            if (Array.isArray(selectedHubs) && selectedHubs.length > 0) {
                // Check multiple ways products can be associated with hubs
                const hasInventoryIdMatch = selectedHubs.includes(product.inventory_id);
                const hasInventoryArrayMatch = product.inventory && product.inventory.some(inv => selectedHubs.includes(inv.id));
                const hasCapacityDetailsMatch = product.capacitydetails && Object.keys(product.capacitydetails).some(hubId => selectedHubs.includes(parseInt(hubId)));
                
                return hasInventoryIdMatch || hasInventoryArrayMatch || hasCapacityDetailsMatch;
            }
            return false; 
        }
    });
        
    // Debug: Log filtered results
    if (user_role === 'customer_engagement_admin') {
        console.log('Campaign Push - Customer Engagement Admin - filteredProducts count:', filteredProducts.length);
        console.log('Campaign Push - Customer Engagement Admin - filteredProducts:', filteredProducts);
    }


    // Initialize inventory_id for admin and supervisor roles
    useEffect(() => {
        if (user_role === 'admin' || user_role === 'supervisor') {
            if (hasSingleAssignedHub) {
                setInventory_id([assignedHubs[0]]);
            } else if (hasMultipleAssignedHubs) {
                setInventory_id(assignedHubs);
            } else if (selectedHub) {
                // Fallback to selected_hub when no assigned list present
                const selectedHubRaw = localStorage.getItem('selected_hub');
                try {
                    const parsedHub = JSON.parse(selectedHubRaw || '[]');
                    if (Array.isArray(parsedHub)) {
                        setInventory_id(parsedHub);
                    } else {
                        setInventory_id([parsedHub]);
                    }
                } catch (error) {
                    setInventory_id([selectedHubRaw]);
                }
            }
        }
    }, [user_role, selectedHub, hasSingleAssignedHub, hasMultipleAssignedHubs]);

    useEffect(() => {
        if (product_id) {
            setProduct_id(null);
        }
    }, [inventory_id]);

    // Check if user can send push notifications
    const canSendPushNotifications = ['super_admin', 'admin', 'supervisor', 'customer_engagement_admin'].includes(user_role);
    
    // Check if user can select products (all roles can now select products)
    const canSelectProducts = ['super_admin', 'admin', 'supervisor', 'customer_engagement_admin'].includes(user_role);

    // Function to fetch stalls for franchise 2 (FoodFix)
    const fetchStallsForFranchise = async (franchiseId) => {
        console.log('fetchStallsForFranchise called with franchiseId:', franchiseId);
        try {
            const url = `stalls/franchise/?franchise_id=${franchiseId}`;
            console.log('Making API call to:', url);
            const response = await CustomAxios.get(url);
            console.log('Stalls API response:', response.data);
            setStalls(response.data || []);
        } catch (error) {
            console.error("Error fetching stalls:", error);
            setStalls([]);
        }
    };

    // Detect franchise type from inventory selection
    const detectFranchiseFromInventory = (inventoryIds) => {
        console.log('detectFranchiseFromInventory called with:', inventoryIds);
        if (!inventoryIds || inventoryIds.length === 0) {
            console.log('No inventory IDs provided');
            return null;
        }
        
        // Find the franchise of the first selected inventory
        const selectedInventory = inventory.find(inv => inventoryIds.includes(inv.value));
        console.log('selectedInventory found:', selectedInventory);
        console.log('franchise_id from selectedInventory:', selectedInventory?.franchise_id);
        
        // Check if franchise_id exists in different possible locations
        const franchiseId = selectedInventory?.franchise_id || selectedInventory?.franchise?.id || selectedInventory?.franchise;
        console.log('Final franchiseId:', franchiseId);
        console.log('selectedInventory.franchise:', selectedInventory?.franchise);
        
        return franchiseId || null;
    };

    // Effect to fetch stalls when inventory changes and franchise is 2
    useEffect(() => {
        console.log('useEffect triggered with inventory_id:', inventory_id);
        if (inventory_id && inventory_id.length > 0) {
            const franchiseId = detectFranchiseFromInventory(inventory_id);
            console.log('Detected franchiseId:', franchiseId);
            setSelectedFranchise(franchiseId);
            
            if (franchiseId === 2) {
                console.log('Franchise is 2, fetching stalls...');
                fetchStallsForFranchise(franchiseId);
            } else {
                console.log('Franchise is not 2, clearing stalls');
                setStalls([]);
                setStall_id(null);
            }
        } else {
            console.log('No inventory_id or empty array');
            setSelectedFranchise(null);
            setStalls([]);
            setStall_id(null);
        }
    }, [inventory_id, inventory]);

    if (!canSendPushNotifications) {
        return (
            <Box sx={{ display: "flex", flexDirection: "column", gap: "20px", padding: "20px" }}>
                <Typography variant="h6" color="error">
                    Access Denied
                </Typography>
                <Typography>
                    You don't have permission to send push notifications.
                </Typography>
            </Box>
        );
    }

    const validate = () => {
        const newErrors = {};
      
        if (!title?.trim()) {
            newErrors.title = 'Title is required';
        }
      
        if (inventory_id?.length == 0) {
            newErrors.hub = 'Please select hubs';
        }

        // For franchise 2 (FoodFix), stall selection is required
        if (selectedFranchise === 2 && (!stall_id || stall_id.length === 0)) {
            newErrors.stall = 'Please select stalls for FoodFix notifications';
        }

        if (!message?.trim()) {
            newErrors.message = 'Message is required';
        }
        
        // Product selection is optional for all roles - they can send to all products or select specific ones
        // No validation required for product selection
      
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };
    const handleSend = async () => {
        if (!validate()) return;
        setLoading(true)
        try {
            const inventoryIds = resolveInventoryIds();
            const payload = {
                title: title,
                message: message,
                ...buildTargetPayload(inventoryIds),
            };
            
            // Only include product_id if a specific product is selected
            if (product_id) {
                payload.product_id = product_id;
            }
            
            await CustomAxios.post(`campaign_push_notification/`, payload);
            setLoading(false)
            toast.current.show({
                severity: 'success',
                summary: 'Success',
                detail: 'Notifications sent Successfully',
                life: 3000,
            });
        } catch (error) {
            console.error("Error sending campaign push notification:", error);
            setLoading(false)
            toast.current.show({ severity: 'error', summary: 'Error', detail: `Failed To send Notifications`, life: 3000 })
        }
    }

    const selectedCountryTemplate = (option, props) => {
        if (option) {
            return (
                <Box sx={{ display: "flex", gap: "10px" }}>
                    <Box component="img" alt={option.name} src={option.image} sx={{ width: '16px', height: "16px", objectFit: "cover" }} />
                    <Box>{option.name}</Box>
                </Box>
            );
        }

        return <span>{props.placeholder}</span>;
    };

    const countryOptionTemplate = (option) => {
        return (
            <Box sx={{ display: "flex", gap: "10px" }}>
                <Box component="img" alt={option.name} src={option.image} sx={{ width: '30px', height: "30px", objectFit: "cover" }} />
                <Box>{option.name}</Box>
            </Box>
        );
    };
    return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: "20px", width: "100%", padding: "30px" }}>
             <Toast ref={toast} />
            <Box sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                <Typography sx={{ fontSize: "16px", fontWeight: "700", lineHeight: "24px" }}>Campaign </Typography>
                <Typography sx={{ fontSize: "14px", fontWeight: "500", lineHeight: "22px" }}>Push Notification</Typography>
            </Box>
            <Box sx={{ height: "100%", width: "100%", backgroundColor: "white", display: "flex", flexDirection: 'column', alignItems: { sm: "center", md: "center" }, padding: "30px", gap: "20px" }}>
                <Box sx={{ width: "100%", display: "flex", flexDirection: "column", gap: "10px" }}>
                    <Typography sx={{ fontSize: "14PX", fontWeight: "400", lineHeight: "22px" }}>Title</Typography>
                    <InputBase value={title} sx={{ backgroundColor: "white", width: "100%", height: "32px", borderRadius: "2px", border: "1px solid", borderColor: "secondary.border", padding: "5px 12px", fontSize: "14px", fontWeight: "400", lineHeight: "22px", color: "black" }} placeholder="Enter title" onChange={(e) => setTitle(e.target.value)} />

                    {errors.title && (
                        <Typography sx={{ color: 'red', fontSize: '12px' }}>{errors.title}</Typography>
                    )}

                </Box>
                <Box sx={{ width: "100%", display: "flex", flexDirection: "column", gap: "10px" }}>
                    <Typography sx={{ fontSize: "14PX", fontWeight: "400", lineHeight: "22px" }}>Message</Typography>
                    <InputTextarea value={message} sx={{ backgroundColor: "white", width: "100%", height: "32px", borderRadius: "2px", border: "1px solid", borderColor: "secondary.border", padding: "5px 12px", fontSize: "14px", fontWeight: "400", lineHeight: "22px", color: "black" }} placeholder="Enter Message" onChange={(e) => setMessage(e.target.value)} />
                    {errors.message && (
                        <Typography sx={{ color: 'red', fontSize: '12px' }}>{errors.message}</Typography>
                    )}
                </Box>
                <Box sx={{ width: "100%", display: "flex", flexDirection: "column", gap: "10px" }}>
                    <Typography sx={{ fontSize: "14PX", fontWeight: "400", lineHeight: "22px" }}>Hubs</Typography>
                    <MultiSelect
                        value={inventory_id}
                        onChange={(e) => setInventory_id(e.target.value)}
                        disabled={(user_role === 'supervisor' || user_role === 'admin') ? hasSingleAssignedHub : false}
                        options={filteredInventory}
                        optionValue="value"
                        optionLabel="name"
                        optionDisabled="disabled"
                        name="invetory_list"
                        placeholder="Select Hubs"
                        className="w-full md:w-14rem"
                        checkmark={true}
                        highlightOnSelect={false}
                    />
                    <Typography sx={{ fontSize: "12px", color: "gray", fontStyle: "italic" }}>
                        {(user_role === 'supervisor' || user_role === 'admin')
                            ? hasSingleAssignedHub
                                ? "Your assigned hub is auto-selected."
                                : "Select from your assigned hubs."
                            : "Select hubs to filter available products."
                        }
                    </Typography>
                    {errors.hub && (
                        <Typography sx={{ color: 'red', fontSize: '12px' }}>{errors.hub}</Typography>
                    )}
                </Box>
                
                <Box sx={{ width: "100%", display: "flex", flexDirection: "column", gap: "10px" }}>
                    <Typography sx={{ fontSize: "14PX", fontWeight: "400", lineHeight: "22px" }}>Notification Related Product</Typography>
                    <Dropdown 
                        onChange={(e) => setProduct_id(e.target.value)} 
                        value={product_id} 
                        options={filteredProducts} 
                        name="product_id" 
                        optionLabel="name" 
                        style={{ zIndex: "1000", height: "32px" }}
                        placeholder="Select Product" 
                        className="w-full md:w-14rem"
                        valueTemplate={selectedCountryTemplate} 
                        itemTemplate={countryOptionTemplate}
                        filter 
                        filterBy="name"
                        disabled={false}
                    />
                    <Typography sx={{ fontSize: "12px", color: "gray", fontStyle: "italic" }}>
                        {user_role === 'supervisor'
                            ? "Supervisors can select specific products from their assigned hubs for targeted notifications, or leave empty to send to all products in selected hubs."
                            : user_role === 'customer_engagement_admin' 
                            ? "Customer Engagement Admin can select specific products from assigned hubs for targeted notifications."
                            : "Select a specific product to send notifications to, or leave empty to send to all products in selected hubs."
                        }
                    </Typography>
                    {errors.product && (
                        <Typography sx={{ color: 'red', fontSize: '12px' }}>{errors.product}</Typography>
                    )}
                </Box>

                {/* Debug Information - Remove after testing */}
                {/* <Box sx={{ width: "100%", display: "flex", flexDirection: "column", gap: "10px", backgroundColor: "#f0f0f0", padding: "10px", borderRadius: "5px" }}>
                    <Typography sx={{ fontSize: "12px", fontWeight: "bold" }}>Debug Info:</Typography>
                    <Typography sx={{ fontSize: "10px" }}>Selected Franchise: {selectedFranchise || 'None'}</Typography>
                    <Typography sx={{ fontSize: "10px" }}>Inventory IDs: {JSON.stringify(inventory_id)}</Typography>
                    <Typography sx={{ fontSize: "10px" }}>Stalls Count: {stalls.length}</Typography>
                    <Typography sx={{ fontSize: "10px" }}>Show Stall Selection: {selectedFranchise === 2 ? 'Yes' : 'No'}</Typography>
                </Box> */}

                {/* Stall Selection for FoodFix (Franchise 2) */}
                {selectedFranchise === 2 && (
                    <Box sx={{ width: "100%", display: "flex", flexDirection: "column", gap: "10px" }}>
                        <Typography sx={{ fontSize: "14PX", fontWeight: "400", lineHeight: "22px" }}>Stalls (FoodFix)</Typography>
                        <MultiSelect
                            value={stall_id}
                            onChange={(e) => setStall_id(e.target.value)}
                            options={stalls}
                            optionValue="id"
                            optionLabel="stall_name"
                            name="stall_list"
                            placeholder="Select Stalls"
                            className="w-full md:w-14rem"
                            checkmark={true}
                            highlightOnSelect={false}
                        />
                        <Typography sx={{ fontSize: "12px", color: "gray", fontStyle: "italic" }}>
                            Select specific stalls to send notifications to FoodFix users who have selected these stalls.
                        </Typography>
                        {errors.stall && (
                            <Typography sx={{ color: 'red', fontSize: '12px' }}>{errors.stall}</Typography>
                        )}
                    </Box>
                )}

                <Box sx={{ width: "100%", display: "flex", flexDirection: "column", gap: "10px" }}>
                    <Typography sx={{ fontSize: "14PX", fontWeight: "400", lineHeight: "22px" }}>Subscription audience</Typography>
                    <Dropdown
                        value={audience}
                        onChange={(e) => {
                            setAudience(e.value);
                            setPreview(null);
                        }}
                        options={audienceOptions}
                        optionLabel="label"
                        optionValue="value"
                        placeholder="Audience"
                        className="w-full md:w-14rem"
                    />
                    <Typography sx={{ fontSize: "12px", color: "gray", fontStyle: "italic" }}>
                        Leave as Default to keep existing hub/stall campaign behavior. Use plan audiences to target subscribers, expired users (rebuy), or non-subscribers (buy plan).
                    </Typography>
                </Box>

                <Box sx={{ width: "100%", display: "flex", flexDirection: "column", gap: "10px" }}>
                    <Typography sx={{ fontSize: "14PX", fontWeight: "400", lineHeight: "22px" }}>Subscription plans (optional)</Typography>
                    <MultiSelect
                        value={selectedPlanIds}
                        onChange={(e) => {
                            setSelectedPlanIds(e.value || []);
                            setPreview(null);
                        }}
                        options={subscriptionPlans}
                        optionValue="id"
                        optionLabel="name"
                        placeholder="Filter by plan(s)"
                        className="w-full md:w-14rem"
                        checkmark={true}
                        highlightOnSelect={false}
                        disabled={audience === 'default' && false}
                    />
                    <Typography sx={{ fontSize: "12px", color: "gray", fontStyle: "italic" }}>
                        Combine with hubs and audience. Empty = all plans for that audience.
                    </Typography>
                </Box>

                {preview ? (
                    <Typography sx={{ fontSize: "13px", fontWeight: 600 }}>
                        {preview.audience ? `${preview.audience} — ` : ''}
                        This notification will be sent to {preview.with_fcm_token} user(s)
                        {preview.eligible_users !== preview.with_fcm_token
                            ? ` of ${preview.eligible_users} matched`
                            : ''}
                    </Typography>
                ) : null}

                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                    <Button
                        variant="outlined"
                        disabled={previewLoading}
                        onClick={handlePreviewCount}
                        sx={{ textTransform: "none" }}
                    >
                        <Typography sx={{ fontSize: { sm: "12px", md: "14px" }, fontWeight: "400", lineHeight: "22px" }}>
                            {previewLoading ? 'Counting...' : 'Preview count'}
                        </Typography>
                    </Button>
                    <Button
                        variant="outlined"
                        disabled={exporting || !preview}
                        onClick={handleDownloadRecipients}
                        title={!preview ? 'Run Preview count first' : undefined}
                        sx={{ textTransform: "none" }}
                    >
                        <Typography sx={{ fontSize: { sm: "12px", md: "14px" }, fontWeight: "400", lineHeight: "22px" }}>
                            {exporting
                                ? 'Preparing...'
                                : !preview
                                    ? 'Preview first to export'
                                    : 'Download recipients (Excel)'}
                        </Typography>
                    </Button>
                    <Button variant="contained" disabled={loading ? true : false} onClick={() => handleSend()} sx={{ textTransform: "none", opacity: loading && .5 }}>
                        <Typography sx={{ fontSize: { sm: "12px", md: "14px" }, fontWeight: "400", lineHeight: "22px", textAlign: "center", color: loading && 'primary.button' }}>
                            {loading ? 'Sending...' : 'Send Notification'}
                        </Typography>
                    </Button>
                </Box>
            </Box>
        </Box>
    )
}